import type { CampaignCreationCapabilities } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { CampaignConfigurationSection } from "@/components/campaign-creation/CampaignConfigurationSection";
import { ProductListingPicker } from "@/components/campaign-creation/ProductListingPicker";
import {
  type CampaignCreationForm,
  ConfigurationFieldRow,
  RadioOption,
  TextField,
} from "@/components/campaign-creation/wizard/ConfigurationFields";
import {
  ManualKeywordTargetEditor,
  ManualProductTargetEditor,
} from "@/components/campaign-creation/wizard/ManualTargetEditors";
import {
  NegativeKeywordEditor,
  NegativeProductEditor,
} from "@/components/campaign-creation/wizard/NegativeTargetEditors";
import type { SectionId } from "@/components/campaign-creation/wizard/wizard-navigation";
import { Switch } from "@/components/ui/switch";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import { AUTO_TARGET_GROUPS, type CampaignCreationFormValues } from "@/lib/campaign-creation-state";
import { cn } from "@/lib/utils";

/** Ad group settings: advertised products, targeting bids and negative targets. */
export function AdGroupSection({
  capabilities,
  form,
  values,
  selectedShopId,
  activeSection,
  currencySymbol,
  enterInput,
  onEnterInputChange,
  negativeKeywordInput,
  onNegativeKeywordInputChange,
  negativeProductInput,
  onNegativeProductInputChange,
  invalidProducts,
  onInvalidProductsChange,
  onCatalogBlockingChange,
  onReconnect,
}: {
  capabilities: CampaignCreationCapabilities;
  form: CampaignCreationForm;
  values: CampaignCreationFormValues;
  selectedShopId: string;
  activeSection: SectionId;
  currencySymbol: string;
  enterInput: string;
  onEnterInputChange: (input: string) => void;
  negativeKeywordInput: string;
  onNegativeKeywordInputChange: (input: string) => void;
  negativeProductInput: string;
  onNegativeProductInputChange: (input: string) => void;
  invalidProducts: CampaignCreationInvalidProduct[];
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  onCatalogBlockingChange: (blocked: boolean) => void;
  onReconnect: () => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <CampaignConfigurationSection title={t("adGroup.title")}>
      <ConfigurationFieldRow label={t("adGroup.name")} required>
        <TextField name="adGroupName" label={t("adGroup.name")} form={form} hideLabel />
      </ConfigurationFieldRow>
      <ConfigurationFieldRow
        id="campaign-section-products"
        label={t("products.title")}
        active={activeSection === "products"}
      >
        <ProductListingPicker
          shopId={selectedShopId}
          marketplaceId={capabilities.marketplaceId}
          selectionLimit={capabilities.limits.maxProducts}
          selected={values.products ?? []}
          onSelectedChange={(products) => {
            const selectedSkus = new Set(products.map((product) => product.sku));
            const selectedAsins = new Set(products.map((product) => product.asin));
            onInvalidProductsChange(
              invalidProducts.filter((invalid) =>
                invalid.sku ? selectedSkus.has(invalid.sku) : selectedAsins.has(invalid.asin),
              ),
            );
            form.setValue("products", products, { shouldDirty: true });
          }}
          enterInput={enterInput}
          onEnterInputChange={onEnterInputChange}
          invalidProducts={invalidProducts}
          onInvalidProductsChange={onInvalidProductsChange}
          onCatalogBlockingChange={onCatalogBlockingChange}
          onReconnect={onReconnect}
        />
      </ConfigurationFieldRow>
      {values.targetingMode === "manual" ? (
        <>
          <ConfigurationFieldRow label={t("manual.targetingType")}>
            <div className="flex max-w-4xl flex-col gap-2">
              <RadioOption
                name="manual-targeting-type"
                value="keyword"
                checked={values.manualTargetingType === "keyword"}
                onChange={() =>
                  form.setValue("manualTargetingType", "keyword", { shouldDirty: true })
                }
                label={t("manual.keyword.title")}
                description={t("manual.keyword.description")}
              />
              <RadioOption
                name="manual-targeting-type"
                value="product"
                checked={values.manualTargetingType === "product"}
                onChange={() =>
                  form.setValue("manualTargetingType", "product", { shouldDirty: true })
                }
                label={t("manual.product.title")}
                description={t("manual.product.description")}
              />
            </div>
          </ConfigurationFieldRow>
          <ConfigurationFieldRow label={t("manual.defaultBid")} required>
            <TextField
              name="manualDefaultBid"
              label={t("manual.defaultBid")}
              form={form}
              inputMode="decimal"
              prefix={currencySymbol}
              hideLabel
              controlClassName="max-w-[160px]"
            />
          </ConfigurationFieldRow>
          <ConfigurationFieldRow
            id="campaign-section-automaticBids"
            label={
              values.manualTargetingType === "keyword"
                ? t("manual.keyword.editorTitle")
                : t("manual.product.editorTitle")
            }
            required
            active={activeSection === "automaticBids"}
          >
            {values.manualTargetingType === "keyword" ? (
              <ManualKeywordTargetEditor
                value={values.manualKeywords}
                defaultBid={values.manualDefaultBid}
                onChange={(value) => form.setValue("manualKeywords", value, { shouldDirty: true })}
              />
            ) : (
              <ManualProductTargetEditor
                value={values.manualProducts}
                defaultBid={values.manualDefaultBid}
                onChange={(value) => form.setValue("manualProducts", value, { shouldDirty: true })}
              />
            )}
          </ConfigurationFieldRow>
        </>
      ) : (
        <ConfigurationFieldRow
          id="campaign-section-automaticBids"
          label={t("bids.title")}
          required
          active={activeSection === "automaticBids"}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <RadioOption
                name="automatic-bid-mode"
                value="default"
                checked={values.bidMode === "default"}
                onChange={() => form.setValue("bidMode", "default", { shouldDirty: true })}
                label={t("bids.default")}
              />
              <RadioOption
                name="automatic-bid-mode"
                value="per_target_group"
                checked={values.bidMode === "per_target_group"}
                onChange={() => form.setValue("bidMode", "per_target_group", { shouldDirty: true })}
                label={t("bids.perGroup")}
              />
            </div>
            {values.bidMode === "default" ? (
              <TextField
                name="defaultBid"
                label={t("bids.defaultAmount")}
                form={form}
                inputMode="decimal"
                prefix={currencySymbol}
                hideLabel
                controlClassName="ml-5 max-w-[160px]"
              />
            ) : (
              <div className="mt-2 max-w-2xl overflow-hidden border border-line">
                <div className="grid grid-cols-[1fr_150px_150px] bg-muted/40 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>{t("bids.targetingGroups")}</span>
                  <span>{t("bids.suggestedBid")}</span>
                  <span>{t("bids.bid")}</span>
                </div>
                {AUTO_TARGET_GROUPS.map((group) => (
                  <div
                    key={group}
                    className="grid grid-cols-[1fr_150px_150px] items-center border-t border-line px-4 py-2 first:border-t-0"
                  >
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={values.groupEnabled[group]}
                        onCheckedChange={(checked) =>
                          form.setValue(`groupEnabled.${group}`, checked, { shouldDirty: true })
                        }
                        aria-label={t(`bids.groups.${group}`)}
                        data-auto-target-group-toggle={group}
                      />
                      <span
                        className={cn(
                          "text-sm",
                          !values.groupEnabled[group] && "text-muted-foreground",
                        )}
                      >
                        {t(`bids.groups.${group}`)}
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground">--</span>
                    <TextField
                      name={`groupBids.${group}` as const}
                      label={t(`bids.groups.${group}`)}
                      form={form}
                      inputMode="decimal"
                      prefix={currencySymbol}
                      hideLabel
                      disabled={!values.groupEnabled[group]}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </ConfigurationFieldRow>
      )}
      <ConfigurationFieldRow
        id="campaign-section-negatives"
        label={t("negatives.keywords.title")}
        active={activeSection === "negatives"}
      >
        <NegativeKeywordEditor
          value={values.negativeKeywords ?? []}
          input={negativeKeywordInput}
          onInputChange={onNegativeKeywordInputChange}
          max={capabilities.limits.maxNegativeKeywords}
          onChange={(value) => form.setValue("negativeKeywords", value, { shouldDirty: true })}
        />
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={t("negatives.products.title")}>
        <NegativeProductEditor
          value={values.negativeProducts ?? []}
          input={negativeProductInput}
          onInputChange={onNegativeProductInputChange}
          max={capabilities.limits.maxNegativeProducts}
          onChange={(value) => form.setValue("negativeProducts", value, { shouldDirty: true })}
        />
      </ConfigurationFieldRow>
    </CampaignConfigurationSection>
  );
}
